import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Button,
    TextField,
    Box
} from '@material-ui/core';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import NewCustomTable from './newCustomTable';

// Estilos usando makeStyles
const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        borderRadius: 12,
        maxWidth: 900,
        width: '90%',
        maxHeight: '85vh',
        margin: 0,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        border: '1px solid #E9ECEF',
        overflow: 'hidden',
    },
    header: {
        padding: '24px 32px',
        borderBottom: '1px solid #E9ECEF',
        backgroundColor: '#F8F9FA',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: 0,
    },
    modalTitle: {
        fontSize: '1.5rem',
        fontWeight: 700,
        color: '#1A1A1A',
        margin: 0,
    },
    closeButton: {
        padding: 0,
        color: '#6C757D',
        '&:hover': {
            color: '#495057',
            backgroundColor: 'transparent',
        },
    },
    content: {
        padding: '24px 32px',
        flex: 1,
        overflow: 'auto',
    },
    footer: {
        padding: '24px 32px',
        borderTop: '1px solid #E9ECEF',
        backgroundColor: '#F8F9FA',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12,
    },
    addButton: {
        backgroundColor: '#00A8E8',
        color: '#FFFFFF',
        '&:hover': {
            backgroundColor: '#0089B5',
        },
    },
    filterContainer: {
        display: 'flex',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(3),
        flexWrap: 'wrap',
    },
    filterInput: {
        flex: '1 1 300px',
    },
    paperContainer: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        borderRadius: 8,
        overflow: 'hidden',
    },
    tableHeader: {
        backgroundColor: '#F8F9FA',
        borderBottom: '1px solid #E9ECEF',
    },
    tableHeaderCell: {
        fontWeight: 600,
        color: '#212529',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
            backgroundColor: 'rgba(0,168,232,0.05)',
        },
    },
    tableRow: {
        borderBottom: '1px solid #E9ECEF',
        '&:hover': {
            backgroundColor: 'rgba(0,168,232,0.03)',
        },
    },
    actionsCell: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    editButton: {
        backgroundColor: '#00A8E8',
        color: '#FFFFFF',
        padding: '4px 10px',
        fontSize: '0.75rem',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#0089B5',
        },
    },
    deleteButton: {
        backgroundColor: '#FF6B00',
        color: '#FFFFFF',
        padding: '4px 10px',
        fontSize: '0.75rem',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#E55A00',
        },
    },
    paginationContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: theme.spacing(2),
        borderTop: '1px solid #E9ECEF',
        color: '#6C757D',
        fontSize: '0.875rem',
    },
    paginationButtons: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    paginationButton: {
        border: '1px solid #DEE2E6',
        color: '#212529',
        fontSize: '0.75rem',
        fontWeight: 600,
        '&:hover:not(:disabled)': {
            borderColor: '#00A8E8',
            color: '#00A8E8',
        },
        '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
    },
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    formLabel: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#212529',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    formInput: {
        '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
                borderColor: '#00A8E8',
            },
        },
    },
    errorInput: {
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#FF6B00',
            },
            '&:hover fieldset': {
                borderColor: '#FF6B00',
            },
        },
    },
    errorText: {
        fontSize: '0.75rem',
        color: '#FF6B00',
        fontWeight: 500,
    },
    formButtons: {
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'flex-end',
        marginTop: theme.spacing(3),
        paddingTop: theme.spacing(3),
        borderTop: '1px solid #E9ECEF',
    },
    saveButton: {
        backgroundColor: '#00A8E8',
        color: '#FFFFFF',
        '&:hover': {
            backgroundColor: '#0089B5',
        },
    },
    cancelButton: {
        border: '1px solid #DEE2E6',
        color: '#212529',
        '&:hover': {
            borderColor: '#00A8E8',
            color: '#00A8E8',
        },
    },
    deleteAlert: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(3),
        borderRadius: 6,
        backgroundColor: '#FFE6E6',
        border: '1px solid #FFCCCC',
        borderLeft: '4px solid #FF6B00',
        animation: '$slideDown 0.3s ease-out',
    },
    deleteAlertContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    deleteAlertMessage: {
        flex: 1,
        fontSize: '0.875rem',
        color: '#721C24',
        fontWeight: 500,
    },
    deleteAlertButtons: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    deleteConfirmButton: {
        backgroundColor: '#FF6B00',
        color: '#FFFFFF',
        '&:hover': {
            backgroundColor: '#E55A00',
        },
    },
    deleteCancelButton: {
        border: '1px solid #FFCCCC',
        color: '#721C24',
        '&:hover': {
            backgroundColor: 'rgba(255,107,0,0.1)',
        },
    },
    '@keyframes slideDown': {
        from: {
            opacity: 0,
            transform: 'translateY(-10px)',
        },
        to: {
            opacity: 1,
            transform: 'translateY(0)',
        },
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(8),
        color: '#6C757D',
    },
}));

// Masks para CPF e CNPJ
const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 14);
    return numbers
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 10) {
        return numbers
            .replace(/(\d{2})(\d)/, '($1) $2')
            .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2');
};

// Validadores
const isValidCPF = (cpf) => {
    const numbers = cpf.replace(/\D/g, '');
    if (numbers.length !== 11 || /^(\d)\1+$/.test(numbers)) return false;

    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(numbers[i]) * (10 - i);
    let digit = (sum * 10) % 11;
    if (digit === 10 || digit === 11) digit = 0;
    if (digit !== parseInt(numbers[9])) return false;

    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(numbers[i]) * (11 - i);
    digit = (sum * 10) % 11;
    if (digit === 10 || digit === 11) digit = 0;
    return digit === parseInt(numbers[10]);
};

const isValidCNPJ = (cnpj) => {
    const numbers = cnpj.replace(/\D/g, '');
    if (numbers.length !== 14 || /^(\d)\1+$/.test(numbers)) return false;

    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    let sum = 0;
    for (let i = 0; i < 12; i++) sum += parseInt(numbers[i]) * weights1[i];
    let digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (digit !== parseInt(numbers[12])) return false;

    sum = 0;
    for (let i = 0; i < 13; i++) sum += parseInt(numbers[i]) * weights2[i];
    digit = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    return digit === parseInt(numbers[13]);
};

const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

// Mock Data
const mockClients = [
    { id: '1', nome: 'Empresa A LTDA', tipo: 'PJ', documento: '12.345.678/0001-90', telefone: '(11) 99999-8888', email: 'contato@empresaa.com.br', endereco: 'Rua das Flores, 123', observacoes: '' },
    { id: '2', nome: 'Comércio B S.A.', tipo: 'PJ', documento: '98.765.432/0001-10', telefone: '(21) 3333-4444', email: 'comercio@b.com', endereco: 'Av. Brasil, 456', observacoes: 'Cliente VIP' },
    { id: '3', nome: 'João Silva', tipo: 'PF', documento: '123.456.789-00', telefone: '(31) 98888-7777', email: 'joao@email.com', endereco: 'Rua Central, 789', observacoes: '' },
];

// Componente DeleteConfirmAlert
const DeleteConfirmAlert = ({ clientName, onConfirm, onCancel, classes, currentLanguage }) => {
    return (
        <div className={classes.deleteAlert}>
            <div className={classes.deleteAlertContent}>
                <div className={classes.deleteAlertMessage}>
                    {currentLanguage.bepiService.shared.shared_clients_delete_warning.replace('{{name}}', clientName)}
                </div>
                <div className={classes.deleteAlertButtons}>
                    <Button
                        variant="outlined"
                        className={classes.deleteCancelButton}
                        onClick={onCancel}
                        size="small"
                        data-testid="button-cancel-delete"
                    >
                        {currentLanguage.bepiService.shared.shared_details_cancel_btn}
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.deleteConfirmButton}
                        onClick={onConfirm}
                        size="small"
                        data-testid="button-confirm-delete"
                    >
                        {currentLanguage.bepiService.shared.shared_clients_delete_btn}
                    </Button>
                </div>
            </div>
        </div>
    );
};

// Componente ClientsTable
const ClientsTable = ({ clients, onEdit, onDelete, classes, currentLanguage }) => {
    const [filterName, setFilterName] = useState('');
    const [filterDocumento, setFilterDocumento] = useState('');

    const filteredClients = clients.filter((client) => {
        const matchName = client.nome.toLowerCase().includes(filterName.toLowerCase());
        const matchDocumento = client.documento.includes(filterDocumento);
        return matchName && matchDocumento;
    });

    const columns = [
        {
            key: 'nome',
            label: currentLanguage.bepiService.shared.shared_clients_table_name,
            sortable: true,
            sortValue: (row) => row.nome,
        },
        {
            key: 'tipo',
            label: currentLanguage.bepiService.shared.shared_clients_table_type,
            sortable: true,
            sortValue: (row) => row.tipo,
        },
        {
            key: 'documento',
            label: currentLanguage.bepiService.shared.shared_clients_table_document,
            sortable: true,
            sortValue: (row) => row.documento,
        },
        {
            key: 'acoes',
            label: currentLanguage.bepiService.shared.shared_clients_table_actions,
            render: (client) => (
                <Box className={classes.actionsCell}>
                    <Button
                        variant="contained"
                        className={classes.editButton}
                        onClick={() => onEdit(client)}
                        startIcon={<EditIcon />}
                        size="small"
                        data-testid={`button-edit-${client.id}`}
                    >
                        {currentLanguage.bepiService.shared.shared_clients_edit_btn}
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.deleteButton}
                        onClick={() => onDelete(client.id)}
                        startIcon={<DeleteIcon />}
                        size="small"
                        data-testid={`button-delete-${client.id}`}
                    >
                        {currentLanguage.bepiService.shared.shared_clients_delete_btn}
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <div>
            <div className={classes.filterContainer}>
                <TextField
                    type="text"
                    placeholder={currentLanguage.bepiService.shared.shared_clients_filter_name_placeholder}
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    variant="outlined"
                    className={classes.filterInput}
                    size="small"
                    data-testid="filter-name"
                />
                <TextField
                    type="text"
                    placeholder={currentLanguage.bepiService.shared.shared_clients_filter_doc_placeholder}
                    value={filterDocumento}
                    onChange={(e) => setFilterDocumento(e.target.value)}
                    variant="outlined"
                    className={classes.filterInput}
                    size="small"
                    data-testid="filter-documento"
                />
            </div>

            <NewCustomTable
                columns={columns}
                rows={filteredClients}
                rowKey="id"
                rowsPerPage={5}
                defaultSortField="nome"
                defaultSortOrder="asc"
                emptyMessage={currentLanguage.bepiService.shared.shared_clients_no_clients_found}
                currentLanguage={currentLanguage}
            />
        </div>
    );
};

// Componente ClientForm
const ClientForm = ({ client, onSave, onCancel, classes, currentLanguage }) => {
    const [formData, setFormData] = useState({
        nome: '',
        tipo: 'PF',
        documento: '',
        telefone: '',
        email: '',
        endereco: '',
        observacoes: '',
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (client) {
            setFormData(client);
        }
    }, [client]);

    const handleTipoChange = (novoTipo) => {
        // Limpa documento ao trocar tipo para evitar dados inválidos
        setFormData({ ...formData, tipo: novoTipo, documento: '' });
        if (errors.documento) setErrors({ ...errors, documento: '' });
    };

    const handleDocumentoChange = (value) => {
        const formatted = formData.tipo === 'PF' ? formatCPF(value) : formatCNPJ(value);
        setFormData({ ...formData, documento: formatted });
        if (errors.documento) setErrors({ ...errors, documento: '' });
    };

    const handleTelefoneChange = (value) => {
        setFormData({ ...formData, telefone: formatPhone(value) });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.nome.trim()) {
            newErrors.nome = currentLanguage.bepiService.shared.shared_clients_name_required;
        }

        if (!formData.tipo) {
            newErrors.tipo = currentLanguage.bepiService.shared.shared_clients_type_required;
        }

        if (!formData.documento.trim()) {
            newErrors.documento = formData.tipo === 'PF' ? currentLanguage.bepiService.shared.shared_clients_cpf_required : currentLanguage.bepiService.shared.shared_clients_cnpj_required;
        } else {
            const isValid = formData.tipo === 'PF'
                ? isValidCPF(formData.documento)
                : isValidCNPJ(formData.documento);
            if (!isValid) {
                newErrors.documento = formData.tipo === 'PF' ? currentLanguage.bepiService.shared.shared_clients_cpf_invalid : currentLanguage.bepiService.shared.shared_clients_cnpj_invalid;
            }
        }

        if (formData.email && !isValidEmail(formData.email)) {
            newErrors.email = currentLanguage.bepiService.shared.shared_clients_email_invalid;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            onSave(formData);
        }
    };

    return (
        <div className={classes.formContainer}>
            <Typography variant="h6" style={{ fontWeight: 700, color: '#1A1A1A', marginBottom: 8 }}>
                {client?.id ? currentLanguage.bepiService.shared.shared_clients_edit_title : currentLanguage.bepiService.shared.shared_clients_new_title}
            </Typography>

            {/* Nome / Razão Social */}
            <div className={classes.formGroup}>
                <Typography className={classes.formLabel}>
                    {currentLanguage.bepiService.shared.shared_clients_table_name} *
                </Typography>
                <TextField
                    type="text"
                    placeholder={currentLanguage.bepiService.shared.shared_clients_name_placeholder}
                    value={formData.nome}
                    onChange={(e) => {
                        setFormData({ ...formData, nome: e.target.value });
                        if (errors.nome) setErrors({ ...errors, nome: '' });
                    }}
                    variant="outlined"
                    fullWidth
                    className={`${classes.formInput} ${errors.nome ? classes.errorInput : ''}`}
                    size="small"
                    error={!!errors.nome}
                    helperText={errors.nome}
                    data-testid="input-nome"
                />
            </div>

            {/* Tipo (PF / PJ) */}
            <div className={classes.formGroup}>
                <Typography className={classes.formLabel}>
                    {currentLanguage.bepiService.shared.shared_clients_table_type} *
                </Typography>
                <Box display="flex" gap={2}>
                    <Button
                        variant={formData.tipo === 'PF' ? 'contained' : 'outlined'}
                        onClick={() => handleTipoChange('PF')}
                        style={{
                            backgroundColor: formData.tipo === 'PF' ? '#00A8E8' : 'transparent',
                            color: formData.tipo === 'PF' ? '#FFFFFF' : '#212529',
                            borderColor: '#DEE2E6',
                            minWidth: 120,
                        }}
                        data-testid="button-tipo-pf"
                    >
                        {currentLanguage.bepiService.shared.shared_clients_pf_label}
                    </Button>
                    <Button
                        variant={formData.tipo === 'PJ' ? 'contained' : 'outlined'}
                        onClick={() => handleTipoChange('PJ')}
                        style={{
                            backgroundColor: formData.tipo === 'PJ' ? '#00A8E8' : 'transparent',
                            color: formData.tipo === 'PJ' ? '#FFFFFF' : '#212529',
                            borderColor: '#DEE2E6',
                            minWidth: 120,
                        }}
                        data-testid="button-tipo-pj"
                    >
                        {currentLanguage.bepiService.shared.shared_clients_pj_label}
                    </Button>
                </Box>
            </div>

            {/* CPF / CNPJ */}
            <div className={classes.formGroup}>
                <Typography className={classes.formLabel}>
                    {formData.tipo === 'PF' ? `${currentLanguage.bepiService.shared.shared_clients_cpf_label} *` : `${currentLanguage.bepiService.shared.shared_clients_cnpj_label} *`}
                </Typography>
                <TextField
                    type="text"
                    placeholder={formData.tipo === 'PF' ? currentLanguage.bepiService.shared.shared_clients_cpf_placeholder : currentLanguage.bepiService.shared.shared_clients_cnpj_placeholder}
                    value={formData.documento}
                    onChange={(e) => handleDocumentoChange(e.target.value)}
                    variant="outlined"
                    fullWidth
                    className={`${classes.formInput} ${errors.documento ? classes.errorInput : ''}`}
                    size="small"
                    error={!!errors.documento}
                    helperText={errors.documento}
                    data-testid="input-documento"
                />
            </div>

            {/* Telefone */}
            <div className={classes.formGroup}>
                <Typography className={classes.formLabel}>
                    {currentLanguage.bepiService.shared.shared_resp_client_phone}
                </Typography>
                <TextField
                    type="text"
                    placeholder={currentLanguage.bepiService.shared.shared_clients_phone_placeholder}
                    value={formData.telefone}
                    onChange={(e) => handleTelefoneChange(e.target.value)}
                    variant="outlined"
                    fullWidth
                    className={classes.formInput}
                    size="small"
                    data-testid="input-telefone"
                />
            </div>

            {/* E-mail */}
            <div className={classes.formGroup}>
                <Typography className={classes.formLabel}>
                    {currentLanguage.bepiService.shared.shared_resp_client_email}
                </Typography>
                <TextField
                    type="email"
                    placeholder={currentLanguage.bepiService.shared.shared_clients_email_placeholder}
                    value={formData.email}
                    onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        if (errors.email) setErrors({ ...errors, email: '' });
                    }}
                    variant="outlined"
                    fullWidth
                    className={`${classes.formInput} ${errors.email ? classes.errorInput : ''}`}
                    size="small"
                    error={!!errors.email}
                    helperText={errors.email}
                    data-testid="input-email"
                />
            </div>

            {/* Endereço */}
            <div className={classes.formGroup}>
                <Typography className={classes.formLabel}>
                    {currentLanguage.bepiService.shared.shared_clients_address_label}
                </Typography>
                <TextField
                    type="text"
                    placeholder={currentLanguage.bepiService.shared.shared_clients_address_placeholder}
                    value={formData.endereco}
                    onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                    variant="outlined"
                    fullWidth
                    className={classes.formInput}
                    size="small"
                    data-testid="input-endereco"
                />
            </div>

            {/* Observações */}
            <div className={classes.formGroup}>
                <Typography className={classes.formLabel}>
                    {currentLanguage.bepiService.shared.shared_resp_form_obs_label}
                </Typography>
                <TextField
                    type="text"
                    placeholder={currentLanguage.bepiService.shared.shared_clients_obs_placeholder}
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    className={classes.formInput}
                    size="small"
                    data-testid="input-observacoes"
                />
            </div>

            <div className={classes.formButtons}>
                <Button
                    variant="outlined"
                    className={classes.cancelButton}
                    onClick={onCancel}
                    data-testid="button-cancel-form"
                >
                    {currentLanguage.bepiService.shared.shared_details_cancel_btn}
                </Button>
                <Button
                    variant="contained"
                    className={classes.saveButton}
                    onClick={handleSubmit}
                    data-testid="button-save-form"
                >
                    {client?.id ? currentLanguage.bepiService.shared.shared_clients_save_changes_btn : currentLanguage.bepiService.shared.shared_clients_create_btn}
                </Button>
            </div>
        </div>
    );
};

// Componente Principal ClientsModal
const ClientsModal = ({ open, onClose, onClientSelect, currentLanguage }) => {
    const classes = useStyles();
    const [clients, setClients] = useState(mockClients);
    const [view, setView] = useState('list'); // 'list', 'form', 'delete-confirm'
    const [selectedClient, setSelectedClient] = useState(null);
    const [deleteConfirmClient, setDeleteConfirmClient] = useState(null);

    const handleAddClient = () => {
        setSelectedClient(null);
        setView('form');
    };

    const handleEditClient = (client) => {
        setSelectedClient(client);
        setView('form');
    };

    const handleSaveClient = (formData) => {
        let savedClient;

        if (selectedClient?.id) {
            // Edit existing client
            savedClient = { ...selectedClient, ...formData };
            setClients(clients.map((c) => (c.id === selectedClient.id ? savedClient : c)));
        } else {
            // Add new client
            savedClient = {
                id: String(Math.max(...clients.map((c) => parseInt(c.id)), 0) + 1),
                ...formData,
            };
            setClients([...clients, savedClient]);
        }

        setView('list');
        setSelectedClient(null);

        // Propaga cliente criado/editado para a tela pai (seleção automática)
        if (onClientSelect && !selectedClient?.id) {
            onClientSelect(savedClient);
            onClose();
        }
    };

    const handleDeleteClick = (clientId) => {
        const client = clients.find((c) => c.id === clientId);
        setDeleteConfirmClient(client);
        setView('delete-confirm');
    };

    const handleConfirmDelete = () => {
        setClients(clients.filter((c) => c.id !== deleteConfirmClient.id));
        setView('list');
        setDeleteConfirmClient(null);
    };

    const getModalTitle = () => {
        switch (view) {
            case 'list':
                return currentLanguage.bepiService.shared.shared_clients_modal_title;
            case 'form':
                return selectedClient?.id ? currentLanguage.bepiService.shared.shared_clients_edit_title : currentLanguage.bepiService.shared.shared_clients_new_title;
            case 'delete-confirm':
                return currentLanguage.bepiService.shared.shared_clients_delete_confirm_title;
            default:
                return currentLanguage.bepiService.shared.shared_clients_modal_title;
        }
    };

    const getFooterContent = () => {
        switch (view) {
            case 'list':
                return (
                    <>
                        <Button
                            variant="contained"
                            className={classes.addButton}
                            onClick={handleAddClient}
                            startIcon={<AddIcon />}
                            data-testid="button-add-client"
                        >
                            {currentLanguage.bepiService.shared.shared_clients_add_btn}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            data-testid="button-close-footer"
                        >
                            {currentLanguage.bepiService.shared.shared_details_close_btn}
                        </Button>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            classes={{
                paper: classes.dialogPaper,
            }}
            BackdropProps={{
                style: {
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    backdropFilter: 'blur(4px)',
                },
            }}
            data-testid="modal-dialog"
        >
            <DialogTitle
                disableTypography
                className={classes.header}
                data-testid="modal-header"
            >
                <Typography variant="h5" className={classes.modalTitle}>
                    {getModalTitle()}
                </Typography>
                <IconButton
                    className={classes.closeButton}
                    onClick={onClose}
                    data-testid="button-close-modal"
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className={classes.content} data-testid="modal-content">
                {view === 'delete-confirm' && deleteConfirmClient && (
                    <DeleteConfirmAlert
                        clientName={deleteConfirmClient.nome}
                        onConfirm={handleConfirmDelete}
                        onCancel={() => {
                            setView('list');
                            setDeleteConfirmClient(null);
                        }}
                        classes={classes}
                        currentLanguage={currentLanguage}
                    />
                )}

                {view === 'list' && (
                    <ClientsTable
                        clients={clients}
                        onEdit={handleEditClient}
                        onDelete={handleDeleteClick}
                        classes={classes}
                        currentLanguage={currentLanguage}
                    />
                )}

                {view === 'form' && (
                    <ClientForm
                        client={selectedClient}
                        onSave={handleSaveClient}
                        onCancel={() => {
                            setView('list');
                            setSelectedClient(null);
                        }}
                        classes={classes}
                        currentLanguage={currentLanguage}
                    />
                )}
            </DialogContent>

            <DialogActions className={classes.footer} data-testid="modal-footer">
                {getFooterContent()}
            </DialogActions>
        </Dialog>
    );
};

ClientsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onClientSelect: PropTypes.func, // Callback chamado ao criar novo cliente (retorna dados do cliente)
    currentLanguage: PropTypes.object,
};

ClientsModal.defaultProps = {
    onClientSelect: undefined,
    currentLanguage: undefined,
};

export default ClientsModal;
